import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  _911_INFORM,
  _911INFORM,
  SECURE911 as SECURE911_SPATIAL_LAYER,
} from '@rsos/constants/spatialLayers';
import { parseWSMessage } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getCallQueueMaxAge } from '@rsos/utils/metaTags';
import {
  setStaticSpatialObjectActiveStatus,
  setStaticSpatialObjectInactiveStatus,
} from '../../../actions/spatialLayers';
import useHasAccessToSecure911 from '../../../hooks/useHasAccessToSecure911';

const ACCEPTED_ORGS = [SECURE911_SPATIAL_LAYER, _911_INFORM];

// Memoized selector to get supported spatial layer organizations
export const selectSupportedSpatialLayerOrgs = createSelector(
  [
    state => state?.spatialLayers?.staticLayers,
    state => state?.spatialLayers?.dynamicLayers,
  ],
  (staticLayers, dynamicLayers) => {
    const staticLayersOrgNames =
      staticLayers?.map(layer => layer?.source) || [];
    const dynamicLayersOrgNames =
      dynamicLayers?.map(layer => layer?.source) || [];
    const _911informOrgNameExists = staticLayersOrgNames.includes(_911INFORM);

    const supportedOrgsSet = new Set(
      [
        ...(_911informOrgNameExists ? [_911_INFORM] : []),
        ...staticLayersOrgNames,
        ...dynamicLayersOrgNames,
      ].filter(orgName => ACCEPTED_ORGS.includes(orgName)),
    );

    return Array.from(supportedOrgsSet);
  },
);

/**
 * Hook that checks whether the generic handling for a spatial layer's spatial
 * object status should take effect based on capabilities and permissions.
 * To support more layers, add to `hasAccessRef.current`.
 * Note: 911inform hardcoded to true for legacy reasons. Data is not supposed
 * to be sent to the FE if the capability and permission aren't enabled.
 */
export const useHasAccessToFeature = () => {
  const hasAccessRef = useRef({});
  const hasAccessToSecure911Feature = useHasAccessToSecure911();

  useEffect(() => {
    hasAccessRef.current = {
      [_911_INFORM]: true,
      [SECURE911_SPATIAL_LAYER]: hasAccessToSecure911Feature,
    };

    return () => (hasAccessRef.current = {});
  }, [hasAccessToSecure911Feature]);

  return hasAccessRef?.current;
};

/**
 * Hook that accesses Hermes to handle setting a spatial object's status through
 * the use of an additional callback function. This in turn changes the spatial
 * object's polygon color. Assumes user has access to the data, otherwise it
 * won't be sent through Hermes. Capabilities and permissions are checked to
 * safeguard against unexpected UI occurrences.
 * NOTE: _911_INFORM is hardcoded into `supportedOrgSet` due to legacy naming
 * conventions
 *
 */
const useHandleActiveSpatialObject = () => {
  const Hermes = window.__RSOS?.hermes?.instance;

  const dispatch = useDispatch();

  const hasAccessToFeature = useHasAccessToFeature();

  const supportedSpatialLayerOrgs = useSelector(
    selectSupportedSpatialLayerOrgs,
  );

  const handleSpatialObjectActiveStatus = useCallback(
    message => {
      const parsedMessageType = parseWSMessage(message);
      const orgName = parsedMessageType.organization;
      if (
        hasAccessToFeature[orgName] &&
        supportedSpatialLayerOrgs.includes(parsedMessageType.organization)
      ) {
        dispatch(setStaticSpatialObjectActiveStatus({ data: message?.body }));

        setTimeout(() => {
          dispatch(
            setStaticSpatialObjectInactiveStatus({ data: message?.body }),
          );
        }, getCallQueueMaxAge() * 1000);
      }
    },
    [dispatch, hasAccessToFeature, supportedSpatialLayerOrgs],
  );

  useEffect(() => {
    if (Hermes) {
      Hermes.addOnMessageCallback({
        handleSpatialObjectActiveStatus: handleSpatialObjectActiveStatus,
      });

      return () => {
        Hermes.removeOnMessageCallback('handleSpatialObjectActiveStatus');
      };
    }
  }, [Hermes, handleSpatialObjectActiveStatus]);

  return null;
};

export default useHandleActiveSpatialObject;
